import { useEffect, useRef, useState } from 'react';
import {
  ActionIcon,
  Alert,
  Box,
  Button,
  Center,
  Group,
  Loader,
  Paper,
  Stack,
  Text,
  Title,
  UnstyledButton,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconArrowLeft, IconBackspace, IconBulb } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';

import { unwrap } from '../api/client';
import { getPuzzle, puzzleHint, savePuzzle } from '../api/gen';
import type { Puzzle, PuzzleCells } from '../api/gen';
import { useOrg, useSession } from '../lib/session';
import { accent } from '../theme';

// The Rätsel: a 4×4 Sudoku the child earned by working a task out alone
// (decision 0006). It keeps the tutor's manners: asked for a Tipp, Sokrates
// says where to look and asks a question, and this page has no solution to
// give away — the server keeps it and says when the board is solved. No timer,
// no score.
//
// The same screen as the tablet's, with a keyboard as well: 1 to 4 write,
// Backspace clears, the arrows move.

const SIDE = 4;

/** The API types the board as sixteen digits; the page keeps it as an array. */
type Board16 = PuzzleCells['cells'];
const RULES =
  'Tippe ein leeres Feld an und dann eine Zahl. In jeder Zeile, jeder Spalte und jedem ' +
  'dicken Viererfeld steht 1, 2, 3 und 4 genau einmal.';
const SOLVED =
  'Geschafft! Jede Zahl steht in jeder Zeile, jeder Spalte und jedem Viererfeld genau ' +
  'einmal. Das hast du selbst herausgefunden.';

/** A pad key: a square a finger can hit, with no side padding to crowd the digit. */
const padKey = {
  variant: 'default',
  w: 60,
  h: 60,
  px: 0,
  radius: 'md',
  styles: { label: { fontSize: 26, fontWeight: 700 } },
} as const;

export function PuzzlePage() {
  const { id = '' } = useParams();
  const org = useOrg();

  const { data, isLoading, error } = useQuery({
    queryKey: ['puzzle', id],
    queryFn: () => unwrap(getPuzzle({ path: { id }, headers: org })),
    // The board is this page's own once loaded; a refetch would undo moves
    // still on their way to the server.
    staleTime: Infinity,
  });

  if (isLoading) {
    return (
      <Center mih="100vh">
        <Loader />
      </Center>
    );
  }
  if (error || !data) {
    return (
      <Center mih="100vh" p="md">
        <Alert color="red">Dieses Rätsel gibt es nicht.</Alert>
      </Center>
    );
  }
  return <Board puzzle={data} />;
}

function Board({ puzzle }: { puzzle: Puzzle }) {
  const org = useOrg();
  const { who } = useSession();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const a = accent(who.kind === 'child' ? who.mode : 'junior');

  const [cells, setCells] = useState<number[]>(puzzle.cells);
  const [solved, setSolved] = useState(puzzle.solved);
  const [selected, setSelected] = useState<number | null>(() => {
    const i = puzzle.cells.indexOf(0);
    return i < 0 ? null : i;
  });
  const [look, setLook] = useState<number[]>([]);
  const [say, setSay] = useState(puzzle.solved ? SOLVED : RULES);
  // Only the newest save may decide the board is solved.
  const saveGen = useRef(0);

  const given = (i: number) => puzzle.grid[i] !== 0;

  const celebrate = () => {
    setSolved(true);
    setSelected(null);
    setLook([]);
    setSay(SOLVED);
    void qc.invalidateQueries({ queryKey: ['puzzles'] });
  };

  const fail = (err: unknown) => notifications.show({ color: 'red', message: (err as Error).message });

  const save = useMutation({
    mutationFn: async ({ board, gen }: { board: number[]; gen: number }) => ({
      gen,
      saved: await unwrap(savePuzzle({ path: { id: puzzle.id }, headers: org, body: { cells: board as Board16 } })),
    }),
    onSuccess: ({ gen, saved }) => {
      if (gen === saveGen.current && saved?.solved) celebrate();
    },
    onError: fail,
  });

  const hint = useMutation({
    mutationFn: () =>
      unwrap(puzzleHint({ path: { id: puzzle.id }, headers: org, body: { cells: cells as Board16 } })),
    onSuccess: (out) => {
      if (!out) return;
      if (out.puzzle.solved) return celebrate();
      setSay(out.hint.text);
      setLook(out.hint.cells);
    },
    onError: fail,
  });

  const write = (digit: number) => {
    if (selected === null || given(selected) || solved) return;
    const board = cells.slice();
    board[selected] = digit;
    setCells(board);
    setLook([]);
    save.mutate({ board, gen: ++saveGen.current });
  };

  const move = (dr: number, dc: number) => {
    const from = selected ?? 0;
    const r = (Math.floor(from / SIDE) + dr + SIDE) % SIDE;
    const c = ((from % SIDE) + dc + SIDE) % SIDE;
    setSelected(r * SIDE + c);
  };

  // The keyboard, for a laptop. Bound to the window rather than a focused
  // element, so it works without the child knowing about focus.
  const keys = useRef({ write, move });
  keys.current = { write, move };
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const k = keys.current;
      if (/^[1-4]$/.test(e.key)) k.write(Number(e.key));
      else if (e.key === 'Backspace' || e.key === 'Delete' || e.key === '0') k.write(0);
      else if (e.key === 'ArrowUp') k.move(-1, 0);
      else if (e.key === 'ArrowDown') k.move(1, 0);
      else if (e.key === 'ArrowLeft') k.move(0, -1);
      else if (e.key === 'ArrowRight') k.move(0, 1);
      else return;
      e.preventDefault();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const thick = '3px solid var(--mantine-color-dark-7)';
  const thin = '1px solid var(--mantine-color-gray-3)';

  return (
    <Center mih="100vh" p="md" style={{ background: a.soft }}>
      <Stack w={440} maw="100%" gap="lg">
        <Group gap="xs">
          <ActionIcon variant="subtle" size="lg" aria-label="Zurück" onClick={() => navigate('/')}>
            <IconArrowLeft size={20} />
          </ActionIcon>
          <Title order={3}>Rätsel</Title>
        </Group>

        <Group align="flex-start" wrap="nowrap" gap="sm">
          <Center
            w={40}
            h={40}
            style={{ borderRadius: '50%', background: 'white', flex: 'none' }}
            aria-hidden
          >
            <Text fw={700} c={a.color}>
              S
            </Text>
          </Center>
          <Paper
            p="sm"
            radius="md"
            style={{ flex: 1, minWidth: 0 }}
            bg={solved ? 'white' : 'gray.1'}
            aria-live="polite"
          >
            <Text size="md" fw={solved ? 600 : 400}>
              {say}
            </Text>
          </Paper>
        </Group>

        <Box
          role="grid"
          aria-label="Sudoku"
          style={{
            display: 'grid',
            gridTemplateColumns: `repeat(${SIDE}, 1fr)`,
            aspectRatio: '1',
            width: '100%',
            border: thick,
            borderRadius: 8,
            overflow: 'hidden',
            background: 'white',
          }}
        >
          {cells.map((v, i) => {
            const row = Math.floor(i / SIDE);
            const col = i % SIDE;
            const isGiven = given(i);
            const isSelected = i === selected && !solved;
            return (
              <UnstyledButton
                key={i}
                aria-label={`Zeile ${row + 1}, Spalte ${col + 1}: ${v === 0 ? 'leer' : v}`}
                onClick={() => !isGiven && !solved && setSelected(i)}
                style={{
                  display: 'grid',
                  placeItems: 'center',
                  fontSize: 'clamp(1.6rem, 7vw, 2.2rem)',
                  fontWeight: 700,
                  fontVariantNumeric: 'tabular-nums',
                  cursor: isGiven || solved ? 'default' : 'pointer',
                  color: isGiven ? 'var(--mantine-color-dark-7)' : `var(--mantine-color-${a.color}-6)`,
                  background: isGiven
                    ? 'var(--mantine-color-gray-1)'
                    : look.includes(i)
                      ? `var(--mantine-color-${a.color}-1)`
                      : 'white',
                  borderRight: col === SIDE - 1 ? undefined : col === 1 ? thick : thin,
                  borderBottom: row === SIDE - 1 ? undefined : row === 1 ? thick : thin,
                  boxShadow: isSelected ? `inset 0 0 0 3px ${a.hex}` : undefined,
                }}
              >
                {v === 0 ? '' : v}
              </UnstyledButton>
            );
          })}
        </Box>

        {solved ? (
          <Button size="md" radius="xl" color={a.color} onClick={() => navigate('/')}>
            Fertig
          </Button>
        ) : (
          <Stack gap="md" align="center">
            <Group gap="xs" justify="center">
              {[1, 2, 3, 4].map((d) => (
                <Button key={d} {...padKey} onClick={() => write(d)}>
                  {d}
                </Button>
              ))}
              <Button {...padKey} aria-label="Löschen" onClick={() => write(0)}>
                <IconBackspace size={24} />
              </Button>
            </Group>
            <Button
              variant="outline"
              radius="xl"
              color={a.color}
              leftSection={<IconBulb size={18} />}
              loading={hint.isPending}
              onClick={() => hint.mutate()}
            >
              Tipp
            </Button>
          </Stack>
        )}
      </Stack>
    </Center>
  );
}
